<template>
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <a href="/" aria-label="Go to home page"
            ><i class="fa fa-home"></i>Home
          </a>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ folderName }}
        </li>
      </ol>
    </nav>

    <div class="card">
      <div class="card-header">
        <div class="card-title">Payslip</div>
      </div>

      <div class="card-body">
        <div class="payslip-request">

          <div class="pr-heading">
            <h2 class="pr-title-main">Payslip Request</h2>
            <p class="pr-intro">
              Generate your payslip or bonus statement to your registered email.
              Requests can be made for any month up to and including last month.
            </p>
          </div>

          <!-- Document Type -->
          <div class="pr-section">
            <div class="pr-label">Document Type</div>
            <div class="pr-grid">
              <div
                class="pr-card"
                :class="{ 'has-data': requestPayslip }"
                @click="toggleType('payslip')"
              >
                <div class="pr-card-top">
                  <div>
                    <div class="fr-title">Payslip</div>
                  </div>
                  <i
                    class="fa fa-check-circle pr-check"
                    :class="{ active: requestPayslip }"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>

              <div
                class="pr-card"
                :class="{ 'has-data': requestBonus }"
                @click="toggleType('bonus')"
              >
                <div class="pr-card-top">
                  <div>
                    <div class="fr-title">Bonus Statement</div>
                  </div>
                  <i
                    class="fa fa-check-circle pr-check"
                    :class="{ active: requestBonus }"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
            <p class="pr-error" v-if="typeError">
              Please select at least one document type.
            </p>
          </div>

          <!-- Month -->
          <div class="pr-section">
            <div class="pr-label">Month</div>
            <div class="pr-month-box" :class="{ error: monthError }">
            <date-time-input
              :input-type="'month'"
              :set-date="selectedMonth"
              :range-date="false"
              :clearable="false"
              :max-date="maxDate"
              @changeDate="
                (obj) => {
                  changeDate('selectedMonth', obj);
                }
              "
            />
              <p class="pr-error" v-if="monthError">
                Please select a valid month.
              </p>
            </div>
          </div>

          <button
            type="button"
            class="pr-submit-btn"
            :class="{ 'is-disabled': !canSubmit }"
            :disabled="isLoading || cooldownRemaining > 0"
            @click="submitRequest"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm mr-2"></span>
            Submit Request<span v-if="cooldownRemaining > 0"> ({{ cooldownRemaining }}s)</span>
          </button>

          <!-- Result -->
          <div
            v-if="resultMessage"
            class="pr-result"
            :class="{
              'is-success': resultStatus === 'success',
              'is-partial': resultStatus === 'partial',
              'is-error': resultStatus === 'error'
            }"
          >
            <i
              class="fa"
              :class="{
                'fa-check-circle': resultStatus === 'success',
                'fa-exclamation-circle': resultStatus === 'partial',
                'fa-times-circle': resultStatus === 'error'
              }"
              aria-hidden="true"
            ></i>
            <span class="pr-result-text">{{ resultMessage }}</span>
          </div>

          <p class="pr-footnote">
            Should you need any assistance, please contact Nina Chen for salary-related inquiries or Bruce Ma for bonus-related inquiries.
            You may also send your inquiry to the PLT mailbox.
            <br /><br />
            Please note that emails are sent every five minutes at fixed intervals (e.g., 00:00, 00:05, 00:10). 
            <br />
            After clicking <strong>Submit Request</strong>, it may take approximately <strong>1 to 5 minutes</strong> for the email to arrive.
          </p>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateTimeInput from '@/components/DateTimeInput.vue';
import dayjs from 'dayjs';

const timeFormat = 'YYYY-MM';
const COOLDOWN_SECONDS = 15;

export default {
  name: 'PayslipRequest',
  components: {
    DateTimeInput,
  },
  data: () => ({
    folderName: '',
    requestPayslip: false,
    requestBonus: false,
    selectedMonth: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    typeError: false,
    monthError: false,
    isLoading: false,
    resultMessage: '',
    resultStatus: '', // 'success' | 'partial' | 'error'
    searchMail: localStorage.getItem('mail'),
    cooldownRemaining: 0,
    cooldownTimer: null,
  }),
  computed: {
    maxDate() {
      // 只能選到上個月（含）
      return dayjs().startOf('month').subtract(1, 'day').format('YYYY-MM-DD');
    },
    canSubmit() {
      return this.requestPayslip || this.requestBonus;
    },
  },
  methods: {
    changeDate(key, obj) {
      let val = obj.value ? dayjs(obj.value).format('YYYY-MM-DD') : null;
      switch (key) {
        case 'selectedMonth': {
          this.selectedMonth = val;
          this.monthError = false; // 選了月份後清掉錯誤提示
          break;
        }
      }
    },
    toggleType(type) {
    if (type === 'payslip') {
      this.requestPayslip = !this.requestPayslip;
    } else if (type === 'bonus') {
      this.requestBonus = !this.requestBonus;
    }
    if (this.canSubmit) {
      this.typeError = false;
    }
  },
    //送出後開始倒數15秒,倒數期間按鈕會被鎖住,避免重複送出
    startCooldown(seconds = COOLDOWN_SECONDS) {
      this.cooldownRemaining = seconds;
      clearInterval(this.cooldownTimer); // 避免重複觸發時計時器疊加
      this.cooldownTimer = setInterval(() => {
        this.cooldownRemaining -= 1;
        if (this.cooldownRemaining <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },
    async submitRequest() {
      this.resultMessage = '';
      this.typeError = false;
      this.monthError = false;

      // 檢查至少選一種文件類型
      if (!this.canSubmit) {
        this.typeError = true;
        return false;
      }
      // 檢查月份是否已選擇
      if (!this.selectedMonth) {
        this.monthError = true;
        return false;
      }

      try {
        this.isLoading = true;
        const token = localStorage.getItem('access_token');
        const month = dayjs(this.selectedMonth).format('YYYY-MM');

        // 組出這次要打的類型清單,例如 ['Pay', 'Bonus'] 或只有其中一個
        const types = [];
        if (this.requestPayslip) types.push('Pay');
        if (this.requestBonus) types.push('Bonus');

        // 用 allSettled,才能分別知道每個類型是成功還是失敗
        const results = await Promise.allSettled(
          types.map((type) => this.getPayroll(token, type, month))
        );

        // 整理出哪些類型有寄出、哪些沒有
        const succeeded = [];
        const failed = [];
        results.forEach((result, index) => {
          const typeLabel = types[index] === 'Pay' ? 'Payslip' : 'Bonus Statement';
          if (result.status === 'fulfilled') {
            succeeded.push(typeLabel);
          } else {
            failed.push(typeLabel);
          }
        });

        // 依照成功／失敗的組合,分成三種結果狀態顯示不同訊息與樣式
        if (failed.length === 0) {
          // 全部類型都成功
          this.resultStatus = 'success';
          this.resultMessage = 'Request successful. Please check your email inbox in 1 to 5 minutes.';
        } else if (succeeded.length === 0) {
          // 全部類型都失敗(HRMS 查無資料)
          this.resultStatus = 'error';
          this.resultMessage = 'No relevant data found in HRMS. If you have any questions, please contact the Payroll Department.';
        } else {
          // 部分成功、部分失敗,例如 Pay 有資料但 Bonus 沒有
          this.resultStatus = 'partial';
          this.resultMessage = `${succeeded.join(' and ')} sent successfully. Please check your email inbox in 1 to 5 minutes. \nHowever, no relevant data found for ${failed.join(' and ')}. If you have any questions, please contact the Payroll Department.`;
        }
      } catch (error) {
        // 保留給非預期例外(如網路中斷、程式錯誤)
        this.resultStatus = 'error';
        this.resultMessage = 'No relevant data found in HRMS. If you have any questions, please contact the Payroll Department.';
      }
      this.isLoading = false;
      this.startCooldown();
    },
  },
  async created() {
    this.folderName = this.$route.name;
  },
  beforeUnmount() {
    clearInterval(this.cooldownTimer);
  },
};
</script>

<style lang="scss" scoped>
.card {
  min-height: calc(100vh - 180px);
}

.card-title {
  font-size: 1.6rem;
  color: #666;
}

.card-body {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 28px;
}

.breadcrumb {
  padding: .75rem 1rem;
  margin-bottom: 1rem;
}

.payslip-request {
  max-width: 700px;
  margin: 0 auto;
  padding: 3% 15px;
}

.pr-heading {
  text-align: center;
  margin-bottom: 3.0rem;
}

.pr-title-main {
  font-size: 28px;
  font-weight: 600;
  color: #A17A5F; 
  margin-bottom: 14px;
}

.pr-intro {
  font-size: 14px;
  color: #888;
  line-height: 1.6;
}

.pr-section {
  margin-bottom: 3.0rem;
}

.pr-label {
  font-size: 15px;
  font-weight: 500;
  color: #A17A5F;
  margin-bottom: 12px;
}

.pr-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

.pr-card {
  background: #ffffff;
  border: 1px solid #d9d9d9;   
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  min-height: 70px;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: #cfae9a;
  }

  &.has-data {
    border-color: #B99075;    
    background: #EBE6E0;
    box-shadow: 0 0 0 1px #B99075 inset;
  }
}

.pr-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.fr-title { font-size: 18px; font-weight: 500; color: #A17A5F; }
.fr-range { font-size: 14px; color: #888; margin-top: 1px; }

.pr-check {
  font-size: 18px;
  color: #cfc7bd;
  transition: color 0.15s ease;

  &.active {
    color: #A17A5F;
  }

  }.pr-result-text {
    white-space: pre-line;
  }

.pr-month-box {
  max-width: 260px;

  :deep(.mx-datepicker) {
    width: 100%;
  }
  :deep(.mx-datepicker .form-control) {
    height: 42px;
  }

  &.error :deep(.mx-datepicker) {
    outline: 1px solid #ff5353;
  }
}

.pr-error {
  color: #ff5353;
  font-size: 13px;
  margin: 8px 0 0;
}

.pr-submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 28px;
  font-size: 15px;
  font-weight: 500;
  color: #ffffff;
  background: #A17A5F;
  border: 1px solid #A17A5F;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;
}

.pr-result {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 1.5rem;

  &.is-success {
    background: #EBE6E0;
    color: #A17A5F;
  }
  &.is-partial {
    background: #FCF3E3;
    color: #B8791E;
  }
  &.is-error {
    background: #fbe9e9;
    color: #c94545;
  }
}

.pr-footnote {
  font-size: 13px;
  color: #999;
  margin-top: 2.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  //text-align: center;
}

@media (max-width: 768px) {
  .pr-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>